import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="register-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16"
    >
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
        Get started — free
      </div>
      <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
        Create your account
      </h1>
      <form onSubmit={onSubmit} className="mt-10 space-y-5">
        <div>
          <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            Full name
          </Label>
          <Input
            id="name"
            data-testid="register-name-input"
            required
            value={form.name}
            onChange={update("name")}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <Label htmlFor="company" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            Company (optional)
          </Label>
          <Input
            id="company"
            data-testid="register-company-input"
            value={form.company}
            onChange={update("company")}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            Work email
          </Label>
          <Input
            id="email"
            data-testid="register-email-input"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            Password
          </Label>
          <Input
            id="password"
            data-testid="register-password-input"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={update("password")}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <Button
          type="submit"
          data-testid="register-submit-btn"
          disabled={loading}
          className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create account
        </Button>
      </form>
      <p className="mt-8 text-sm text-slate-500">
        Already have an account?{" "}
        <Link
          to="/login"
          data-testid="register-to-login-link"
          className="font-medium text-blue-700 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </main>
  );
}
